<template>
  <v-app>
    <app-top-bar :class="hasBackgroundImage ? '' : 'bg-background'" :path-return="pathPage">{{
      $t("client.pageTitle")
    }}</app-top-bar>
    <v-main :style="mainStyle" :class="hasBackgroundImage ? '' : 'bg-background'">
      <v-container v-if="!showComplete">
        <h1 class="page-title mb-6 text-on-surface-variant">{{ $t("client.mainTitle") }}</h1>
        <p class="mb-6 text-on-surface-variant">
          {{ $t("client.description") }}
        </p>

        <v-form ref="userForm" lazy-validation>
          <v-text-field
            v-model.trim="user.name"
            class="mb-1 text-on-surface-variant"
            variant="underlined"
            :label="$t('client.nameLabel')"
            required
            :rules="nameInputRules"
            type="text"
            color="primary"
            background-color="surface"
          />

          <v-text-field
            v-model="user.phone"
            v-mask="['+55 (##) ####-####', '+55 (##) #####-####']"
            class="mb-1 text-on-surface-variant"
            variant="underlined"
            :label="$t('client.phoneLabel')"
            required
            :rules="phoneInputRules"
            type="tel"
            color="primary"
            background-color="surface"
          />

          <v-text-field
            v-model.trim="user.email"
            class="mb-1 text-on-surface-variant"
            variant="underlined"
            :label="$t('client.emailLabel')"
            required
            :rules="emailInputRules"
            type="email"
            color="primary"
            background-color="surface"
          />
        </v-form>
      </v-container>
      <v-container v-if="showComplete">
        <phase-completed />
      </v-container>
    </v-main>

    <app-footer>
      <continue-footer-button
        :disabled="!isUserFormValid"
        :loading="isAdvancing"
        @click="advance"
      />
    </app-footer>
  </v-app>
</template>

<script setup lang="ts">
  import { validate as validateEmail } from "email-validator";
  import { isPossiblePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";
  import _ from "lodash";
  import { computed, reactive, ref, useTemplateRef } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";
  import { mask as vMask } from "vue-the-mask";

  import { usePageBackground } from "@/composables/usePageBackground";
  import { useStepFlow } from "@/composables/useStepFlow";
  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";

  const { t } = useI18n();

  interface User {
    name: string | null;
    phone: string | null;
    email: string | null;
  }

  const app = useAppStore();
  const api = useApiStore();
  const router = useRouter();
  const { mainStyle, hasBackgroundImage } = usePageBackground();
  const pathPage = "/client-info";

  const {
    isAdvancing,
    showComplete,
    advance: advanceFlow,
  } = useStepFlow(pathPage, () => isComplete.value);

  const user = reactive({
    name: api.client?.name ?? null,
    phone: api.client?.phone ?? null,
    email: api.client?.email ?? null,
  });

  const userFormRef = useTemplateRef("userForm");
  const isUserFormValid = computed(() => isComplete.value || userFormRef.value?.isValid || false);
  const isComplete = computed(() => {
    return api.isItemComplete("name") && api.isItemComplete("email") && api.isItemComplete("phone");
  });

  async function advance() {
    if (isComplete.value) {
      try {
        await advanceFlow();
      } catch (e) {
        console.error(e);
      }
      return;
    }
    try {
      if (api.insuranceCode) {
        await advanceFlow(async () => {
          const userId = await api.createUser(user.name ?? "", user.email ?? "", user.phone ?? "");
          api.client.id = userId;
          api.client.name = user.name;
          api.client.phone = user.phone;
          api.client.email = user.email;
        });
        return;
      }
      isAdvancing.value = true;
      await api.createClient(user.name ?? "", user.email ?? "", user.phone ?? "");
      router.push({ path: "/client-verification" });
    } catch (e) {
      console.error(e);
      isAdvancing.value = false;
    }
  }

  function extractWords(text) {
    return text.split(" ").filter((word) => word.length > 2);
  }

  function countWordsLongerThan2(text) {
    return extractWords(text).length;
  }

  function minWordLength(text) {
    if (text == null) return 0;

    return _.min(_.map(_.filter(extractWords(text)), (word) => word.length));
  }

  const nameInputRules = [
    (v: string) => !!v || t("client.nameRequired"),
    (v: string) => (v && v.length > 2) || t("client.nameMinLength"),
    (v: string) => countWordsLongerThan2(v) >= 2 || t("client.nameMinWords"),
    (v: string) => minWordLength(v) > 2 || t("client.nameMinWordLength"),
  ];

  const emailInputRules = [
    (v: string) => !!v || t("client.emailRequired"),
    (v: string) => validateEmail(v) || t("client.emailInvalid"),
  ];

  const phoneInputRules = [
    (v: string) => !!v || t("client.phoneRequired"),
    (v: string) => isPossiblePhoneNumber(v) || t("client.phoneInvalid"),
    (v: string) => isValidPhoneNumber(v) || t("client.phoneInvalid"),
  ];
</script>

<style lang="scss" scoped>
  p {
    margin-bottom: 1.25rem;
  }
</style>
